import React from 'react';
import { motion } from 'motion/react';
import { MapPin, ExternalLink } from 'lucide-react';
import { BDAY_EASE } from '../theme';
import type { BirthdayContent } from '../content';
import { RsvpForm } from '@/src/modules/rsvp';

export function RsvpSection({
  venue,
  rsvp,
  invitationId,
  isLive,
}: {
  venue: BirthdayContent['venue'];
  rsvp: BirthdayContent['rsvp'];
  invitationId?: string | null;
  isLive?: boolean;
}) {
  return (
    <section
      className="relative py-24 px-6 overflow-hidden text-center"
      style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e3a5f 60%, #1d4ed8 100%)' }}
    >
      {/* glow blob */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-xl mx-auto flex flex-col items-center gap-8">
        {/* venue */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.65, ease: BDAY_EASE }}
          className="bg-white/8 backdrop-blur-sm border border-blue-400/20 rounded-2xl px-8 py-6 w-full"
        >
          <div className="flex items-center justify-center gap-2 text-blue-400 text-xs font-bold uppercase tracking-widest mb-3">
            <MapPin size={14} />
            Venue
          </div>
          <h3 className="text-white font-bold text-xl mb-1">{venue.name}</h3>
          {venue.address !== venue.name && (
            <p className="text-blue-200 text-sm">{venue.address}</p>
          )}
          {venue.mapsLink && (
            <a
              href={venue.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-3 text-blue-400 text-xs hover:text-blue-200 transition-colors"
            >
              View on Maps <ExternalLink size={11} />
            </a>
          )}
        </motion.div>

        {/* custom message */}
        {rsvp.message && (
          <motion.blockquote
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="text-blue-200 italic text-base leading-relaxed border-l-2 border-blue-400/40 pl-5 text-left"
          >
            "{rsvp.message}"
          </motion.blockquote>
        )}

        {/* RSVP — external link OR native inline form */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2, ease: BDAY_EASE }}
          className="w-full"
        >
          {rsvp.link ? (
            <a
              href={rsvp.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-10 py-4 bg-gradient-to-r from-blue-500 to-blue-700 text-white font-bold uppercase tracking-widest text-sm rounded-full shadow-xl shadow-blue-900/60 hover:from-blue-400 hover:to-blue-600 transition-all"
            >
              <ExternalLink size={16} />
              🎉 Count Me In!
            </a>
          ) : (
            <RsvpForm invitationId={invitationId} isLive={isLive} />
          )}
        </motion.div>

        {/* footer note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="text-blue-400/60 text-xs"
        >
          We can't wait to celebrate with you ✨
        </motion.p>
      </div>
    </section>
  );
}
